<script setup>
  import { computed, ref, useId } from 'vue';
  import { useI18n } from 'vue-i18n';
  import SectionToggle from './SectionToggle.vue';

  const { t } = useI18n();
  const i18nScope = 'components.sales_dashboard';

  const props = defineProps({
    title: { type: String, required: true },
    // Explains how the metric is calculated, surfaced as a tooltip and as
    // help text tied to the region via aria-describedby.
    help: { type: String, default: null },
    // Plain-language summary of the whole series, so a screen reader gets the
    // shape of the data without stepping through every point.
    summary: { type: String, default: null },
    // Column headings for the fallback table
    tableColumns: { type: Array, default: () => [] },
    // [[cell, cell], ...]
    tableRows: { type: Array, default: () => [] },
    tableCaption: { type: String, default: null },
    // null leaves the card always open with no toggle; a boolean makes it
    // collapsible, with the parent owning the state through the toggle event.
    collapsed: { type: Boolean, default: null }
  });

  const emit = defineEmits(['toggle']);

  const helpId = useId();
  const bodyId = useId();
  const tableOpen = ref(false);

  const hasTable = computed(() => props.tableColumns.length > 0 && props.tableRows.length > 0);
</script>

<template>
  <section class="card mb-4" :aria-label="title">
    <div class="card-header d-flex justify-content-between align-items-center gap-2">
      <h2 class="h5 mb-0 d-flex align-items-center gap-2">
        {{ title }}
        <button
          v-if="help"
          type="button"
          class="btn btn-link p-0 border-0 help-trigger"
          data-bs-toggle="tooltip"
          :data-bs-title="help"
          :aria-label="help"
        >
          <i class="bi bi-info-circle" aria-hidden="true"></i>
        </button>
      </h2>

      <div class="d-flex align-items-center gap-2">
        <button
          v-if="hasTable && !collapsed"
          type="button"
          class="btn btn-sm btn-outline-secondary"
          :aria-expanded="tableOpen"
          @click="tableOpen = !tableOpen"
        >
          {{ tableOpen ? t(`${i18nScope}.hide_data_table`) : t(`${i18nScope}.show_data_table`) }}
        </button>
        <SectionToggle v-if="collapsed !== null" :collapsed="collapsed" :section="title" :controls="bodyId" @toggle="emit('toggle')" />
      </div>
    </div>

    <div v-if="!collapsed" :id="bodyId" class="card-body">
      <p v-if="help" :id="helpId" class="visually-hidden">{{ help }}</p>

      <!-- The chart itself is decorative to assistive tech; this summary and
           the table below carry the same information in text. -->
      <div :aria-describedby="help ? helpId : undefined">
        <p v-if="summary" class="visually-hidden">{{ summary }}</p>
        <slot />
      </div>

      <div v-if="hasTable" class="table-responsive mt-3" :class="{ 'visually-hidden': !tableOpen }">
        <table class="table table-sm table-striped mb-0">
          <caption class="visually-hidden">{{ tableCaption || title }}</caption>
          <thead>
            <tr>
              <th v-for="(column, index) in tableColumns" :key="column" :class="{ 'text-end': index > 0 }" scope="col">
                {{ column }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in tableRows" :key="row[0]">
              <th scope="row" class="fw-normal">{{ row[0] }}</th>
              <td v-for="(cell, index) in row.slice(1)" :key="index" class="text-end">{{ cell }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<style scoped>
  .help-trigger {
    color: #6c757d;
    line-height: 1;
  }

  .help-trigger:hover,
  .help-trigger:focus {
    color: #0d6efd;
  }
</style>
